import { CommonModule } from "@angular/common";
import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { Store } from "@ngrx/store";
import { AddTestSuiteComponent } from "../../modal-templates/add-test-suite/add-test-suite.component";
import { BehaviorSubject, combineLatest, map, Observable, of, switchMap, take } from "rxjs";
import { ExpandableAccordionComponent } from "../../common/expandable-accordion/expandable-accordion.component";
import { LoadingComponent } from "../../common/loading/loading.component";
import { TestCase } from "../../../core/models/testCase";
import {
  selectAllTestCasesByVersionId,
  selectAllTestCaseVersions,
  selectLoadingTestCases,
} from "../../../core/ngrx/selectors/testcase-generator.selectors";
import { TcgTestSuiteRow } from "../tcg-test-suites/tcg-test-suites.component";
import { RestService } from "../../../core/services/rest.service";
import { MessageEnum } from "../../../core/utils/message.enum";
import { ToasterService } from "../../../core/services/toaster-service";
import { ProjectService } from "../../../core/services/project/project.service";
import { CommonService } from "../../../core/services/common.service";
import { DeleteConfirmationComponent } from "../../modal-templates/delete-confirmation/delete-confirmation.component";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { AddMissingTestCaseModalComponent } from "../../modal-templates/add-missing-test-case-modal/add-missing-test-case-modal.component";
import { TcgTraceabilityModalComponent } from "../../modal-templates/tcg-traceability-modal/tcg-traceability-modal.component";
import { TestCaseGeneratorActions } from "../../../core/ngrx/actions/testcase-generator.actions";
import { ConnectionActions } from "../../../core/ngrx/actions/connection.actions";
import { selectConnections } from "../../../core/ngrx/selectors/connection.selectors";
import {
  PromptFilterModalComponent,
  TcgFilterResult,
  TcgFilterState,
} from "../../modal-templates/prompt-filter-modal/prompt-filter-modal.component";
import { TcgStoryDetailModalComponent } from "../../modal-templates/tcg-story-detail-modal/tcg-story-detail-modal.component";
import { userstoryDetails } from "../../../core/dummy/dummy-data";

interface AccordionColumn {
  mapping_key: string;
  display_name: string;
  isVisible: boolean;
}

/** Always-visible row-header columns (collapsed row summary). */
const HEADER_COLUMNS: AccordionColumn[] = [
  { mapping_key: "userStoryId",  display_name: "Story ID",     isVisible: true },
  { mapping_key: "testCaseId",   display_name: "Test Case ID", isVisible: true },
  { mapping_key: "testCaseType", display_name: "Test Type",    isVisible: true },
  { mapping_key: "priority",     display_name: "Priority",     isVisible: true },
  { mapping_key: "testCase",     display_name: "Test Case",    isVisible: true },
];

/**
 * Fixed ordered list of fields shown in the expanded detail pane.
 * Matches classic TCG more-details: Test Type → Sub Type → Traceability →
 * Prerequisites → Test Steps → Sanitized Test Steps → Expected Results
 * (Feedback via thumbs actions).
 */
const EXPAND_COLUMNS: AccordionColumn[] = [
  { mapping_key: "testCaseType",        display_name: "Test Type",             isVisible: false },
  { mapping_key: "subType",             display_name: "Sub Type",              isVisible: false },
  { mapping_key: "traceability",        display_name: "Traceability",          isVisible: false },
  { mapping_key: "prerequisites",       display_name: "Prerequisites",         isVisible: false },
  { mapping_key: "testScenario",        display_name: "Test Steps",            isVisible: false },
  { mapping_key: "sanitizedTestSteps",  display_name: "Sanitized Test Steps",  isVisible: false },
  { mapping_key: "expectedResult",      display_name: "Expected Results",      isVisible: false },
];

/** Map store test case → accordion row (`key` → `userStoryId`). */
function mapRow(tc: any): any {
  const row = { ...tc };
  row.userStoryId = tc.userStoryId || tc.key || tc.storyId || '';
  const type = formatTestType(tc.testType ?? tc.testCaseType ?? tc.type ?? '');
  row.testType = type;
  row.testCaseType = type;
  row.subType = tc.subType ?? tc.subtype ?? tc.testSubType ?? tc.testCaseSubType ?? '';
  row.priority = tc.priority ?? '';
  // BPD uses businessRuleRef instead of ALM-style traceability text
  row.traceability = tc.traceability || tc.businessRuleRef || '';
  row.sanitizedTestSteps = tc.sanitizedTestSteps ?? '';
  row.source = tc.source ?? '';
  delete row.key;
  return row;
}

function formatTestType(raw: string): string {
  const value = String(raw ?? '').trim();
  if (!value) return '';
  const normalized = value.toLowerCase().replace(/[_-]+/g, ' ');
  if (normalized.includes('regression')) return 'Regression';
  if (normalized.includes('sanity')) return 'Sanity';
  if (normalized.includes('smoke')) return 'Smoke';
  if (normalized.includes('non functional')) return 'Non-functional';
  return value;
}

/** Fixed column list — header visible columns + ordered expand columns. */
function buildColumns(_rows: any[]): AccordionColumn[] {
  return [...HEADER_COLUMNS, ...EXPAND_COLUMNS];
}

/** Returns true when a row matches the active TCG filter (safe when priority is missing). */
function matchesTcgFilter(row: any, filter: TcgFilterState): boolean {
  const storyMatch =
    !filter.userStory || (row.userStoryId ?? "") === filter.userStory;

  if (!filter.priority) {
    return storyMatch;
  }

  const rowPriority = (row.priority ?? "").toString().trim();
  if (!rowPriority) {
    return false;
  }

  const priorityMatch =
    rowPriority.toUpperCase() === filter.priority.toUpperCase();
  return storyMatch && priorityMatch;
}

/**
 * Drill-down view shown when a "X Test Cases" action link is clicked on the
 * TCG "Test Suites" list. Loads real test case data via the NgRx store.
 */
@Component({
  selector: "app-tcg-test-case-detail",
  standalone: true,
  imports: [CommonModule, ExpandableAccordionComponent, LoadingComponent],
  templateUrl: "./tcg-test-case-detail.component.html",
  styleUrl: "./tcg-test-case-detail.component.scss",
})
export class TcgTestCaseDetailComponent implements OnInit, OnChanges {
  private readonly store = inject(Store);
  private readonly toaster = inject(ToasterService);
  private readonly projectService = inject(ProjectService);
  private readonly commonService = inject(CommonService);
  private readonly modalService = inject(NgbModal);
  private readonly removedTestCaseIds$ = new BehaviorSubject<Set<string>>(new Set());
  private readonly activeFilter$ = new BehaviorSubject<TcgFilterState | null>(null);

  /** True when user story and/or priority filter is applied. */
  isFilterActive$ = this.activeFilter$.pipe(
    map((filter) => !!(filter?.userStory || filter?.priority))
  );

  @Input() suite: TcgTestSuiteRow | null = null;
  /** When true the "Add to Test Suite" button is shown (BPD flow only). */
  @Input() showAddToSuite = false;
  /** When true, "Add to Suite" delegates to the parent via actionEmit instead
   *  of opening the generic AddTestSuiteComponent directly. */
  @Input() isImportFlow = false;
  /** Emitted only when isImportFlow=true — parent opens ImportAddToSuiteModalComponent. */
  @Output() importAddToSuite = new EventEmitter<any[]>();
  /** Emitted when the user picks "Import Test Cases" from the dropdown — parent opens the import modal.
   *  Carries the current suite context so the parent can add extracted cases directly into this suite. */
  @Output() importTestCases = new EventEmitter<{ suiteId: string; suiteName: string; llmConnectionId: string | null }>();
  @Output() back = new EventEmitter<void>();
  projectId: string = "";
  llmConnection: any;
  /** True while Update from Feedback API is in flight. */
  isUpdatingFeedback = false;
  loadingTestCases$ = this.store.select(selectLoadingTestCases);
  testCaseVersions$!: Observable<any[]>;

  /** Unique story keys (`key`) from current version test cases, excluding locally deleted rows. */
  storyIds$: Observable<string[]> = combineLatest([
    this.store.select(selectAllTestCasesByVersionId),
    this.removedTestCaseIds$,
  ]).pipe(
    map(([cases, removedIds]) =>
      [...new Set(
        (cases ?? [])
          .filter((tc: any) => !removedIds.has(tc._id))
          .map((tc: any) => tc.key)
          .filter((key): key is string => !!key)
      )]
    )
  );

  accordionData$: Observable<{ columns: AccordionColumn[]; rows: any[] }> = combineLatest([
    this.store.select(selectAllTestCasesByVersionId),
    this.removedTestCaseIds$,
    this.activeFilter$,
  ]).pipe(
    map(([cases, removedIds, filter]) => {
      const allRows = (cases ?? [])
        .filter((tc: any) => !removedIds.has(tc._id))
        .map(mapRow)
        .sort((a: any, b: any) => {
          const TYPE_ORDER: Record<string, number> = {
            HAPPY: 1, ERROR: 2, EXCEPTION: 3, EXPLORATORY: 4,
          };

          // Extract numeric part from userStoryId (e.g. "AF-160" → 160, "AF-4" → 4)
          const storyNum = (storyId: string) => {
            const m = (storyId ?? '').match(/(\d+)$/);
            return m ? parseInt(m[1], 10) : 0;
          };

          // Extract type and trailing number from testCaseId
          const parseId = (id: string) => {
            const m = (id ?? '').match(/_(HAPPY|ERROR|EXCEPTION|EXPLORATORY|BC|EDGE)_(\d+)$/i);
            return {
              typeOrder: m ? (TYPE_ORDER[(m[1] ?? '').toUpperCase()] ?? 99) : 99,
              num: m ? parseInt(m[2], 10) : 0,
            };
          };

          // 1. Group by userStoryId (numeric sort: AF-4 < AF-5 < AF-95 < AF-160)
          const storyCmp = storyNum(a.userStoryId) - storyNum(b.userStoryId);
          if (storyCmp !== 0) return storyCmp;

          const pa = parseId(a.testCaseId);
          const pb = parseId(b.testCaseId);

          // 2. Within same story: HAPPY → ERROR → EXCEPTION → EXPLORATORY
          if (pa.typeOrder !== pb.typeOrder) return pa.typeOrder - pb.typeOrder;

          // 3. Within same story + type: by trailing number
          return pa.num - pb.num;
        });

      let rows = allRows;
      if (filter && (filter.userStory || filter.priority)) {
        rows = allRows.filter((row) => matchesTcgFilter(row, filter));
      }

      return { columns: buildColumns(allRows), rows };
    })
  );

  loading$: Observable<boolean> = this.store.select(selectLoadingTestCases);

  constructor(private readonly restService: RestService){}

  ngOnInit(): void {
    this.projectService.getProjectId().subscribe(id => {
      if (id) {
        this.projectId = id;
        this.store.dispatch(ConnectionActions.getConnections({ id }));
        this.store.dispatch(
          TestCaseGeneratorActions.loadTestCaseVersions({ projectId: id })
        );
      }
    });

    this.commonService.llmConnection$.subscribe((connection) => {
      this.llmConnection = connection;
    });

    this.testCaseVersions$ = this.store.select(selectAllTestCaseVersions).pipe(
      map((connections) =>
        connections.map((connection: any) => ({
          value: connection,
          label: connection.versionName,
        }))
      )
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["suite"]) {
      this.removedTestCaseIds$.next(new Set());
      this.activeFilter$.next(null);
    }
  }
  
  onBackClick(): void {
    this.back.emit();
  }

  /** Origin values that do not carry user-story context → hide User Story filter. */
  private static readonly ORIGIN_NO_STORY = new Set([
    'EXCEL', 'CSV', 'ZEPHYR_ESSENTIAL', 'MANUAL',
  ]);

  onFilter(): void {
    combineLatest([
      this.storyIds$.pipe(take(1)),
      this.activeFilter$.pipe(take(1)),
      this.store.select(selectAllTestCasesByVersionId).pipe(take(1)),
    ]).subscribe(([storyIds, activeFilter, cases]) => {
      const isBpdSource =
        (cases?.length ?? 0) > 0 &&
        (cases ?? []).every(
          (tc: any) => String(tc?.source ?? "").toUpperCase() === "BPD"
        );

      const hasOriginWithoutStory =
        (cases?.length ?? 0) > 0 &&
        (cases ?? []).some(
          (tc: any) =>
            TcgTestCaseDetailComponent.ORIGIN_NO_STORY.has(
              String(tc?.origin ?? "").toUpperCase()
            )
        );

      // Hide User Story for BPD, import flow, or any known non-story origin
      const hideUserStory = isBpdSource || this.isImportFlow || hasOriginWithoutStory;

      const modalRef = this.modalService.open(PromptFilterModalComponent, {
        size: "lg",
        centered: true,
        backdrop: "static",
      });

      modalRef.componentInstance.mode = "tcg";
      modalRef.componentInstance.tcgShowUserStory = !hideUserStory;
      modalRef.componentInstance.tcgUserStories = hideUserStory
        ? []
        : storyIds.map((id) => ({ label: id, value: id }));

      let initialFilter = activeFilter;
      if (hideUserStory && activeFilter) {
        initialFilter = { userStory: "", priority: activeFilter.priority ?? "" };
      }
      modalRef.componentInstance.initialTcgFilter = initialFilter;
      modalRef.componentInstance.onFilterReset = () => {
        this.activeFilter$.next(null);
      };

      modalRef.result.then(
        (result: TcgFilterResult) => {
          if (result?.reset) {
            this.activeFilter$.next(null);
            return;
          }
          const userStory = hideUserStory ? "" : (result?.userStory ?? "");
          const priority = result?.priority ?? "";
          if (!userStory && !priority) {
            this.activeFilter$.next(null);
            return;
          }
          this.activeFilter$.next({ userStory, priority });
        },
        () => {
          /* dismissed */
        }
      );
    });
  }

  onDownload(event:any): void {
    const formattedData = event?.data?.map((row: any) => ({
      _id: row._id,
      key: row.userStoryId || '',
      storydesc: row.storydesc || '',
      testCaseId: row.testCaseId || '',
      testCase: row.testCase || '',
      prerequisites: row.prerequisites || '',
      testScenario: row.testScenario || '',
      expectedResult: row.expectedResult || '',
      storysummary:row?.storysummary || '',
      // priority:row?.priority || '',
      testCaseType:row?.testCaseType || row?.testType || '',
      subType:row?.subType || '',
    }));

    const payload = {
      testSuiteName: this.suite?.testSuiteName ?? this.suite?.id ?? "Test_Suite_Download",
      selectedTestCaseCount: formattedData.length,
      testCaseReportDetails: formattedData,
    };
    this.restService.downloadExcel(this.projectId, payload).subscribe({
      next: (response: any) => {
        window.open(response?.data, "_blank");
      },
      error: (error: any) => {
        let errMessage = error?.message ? error?.message : MessageEnum.EXCEL_DOWNLOAD_FAILURE;
        this.toaster.pop({ type: 'error', text: errMessage});
      }
    });
  }

  onDelete(event:any): void {
    console.log("delete",event?.data);
    let modalRef = this.modalService.open(DeleteConfirmationComponent, {
      windowClass:"delete-confirmation-window",
      centered: true,
      size: "sm",
      backdrop: "static",
    });
    modalRef.result.then((result: any) => {
      if (result) {
        this.removeTestCase(event?.data);
      }
    });
  }

  removeTestCase(data: any) {
    const versionId = this.suite?.id;
    if (!versionId) {
      return;
    }

    this.loadingTestCases$ = of(true);
    this.restService
      .deleteTestCase(
        this.projectId,
        versionId,
        data
      )
      .subscribe({
        next: (response: any) => {
          console.log("Delete Response:", response);
          
          if (response?.status === 201) {
            const skipped = response?.data?.skippedTestcases || [];
            const deleted: string[] = response?.data?.deletedTestcases || [];

            data.forEach((row: any) => {
              const id = row._id;

              if (skipped.includes(id)) {
                this.toaster.pop({
                  type: "error",
                  text: MessageEnum.TEST_CASE_ALREADY_MAPPED,
                });
              } else if (deleted.includes(id)) {
                this.toaster.pop({
                  type: "success",
                  text: MessageEnum.TEST_CASE_DELETION_SUCCESS,
                });
              }
            });

            if (deleted.length > 0) {
              const removed = new Set(this.removedTestCaseIds$.value);
              deleted.forEach((id) => removed.add(id));
              this.removedTestCaseIds$.next(removed);
            }

            this.loadingTestCases$ = of(false);
          }
        },
        error: (error: any) => {
          console.error("Delete API Error:", error);
          this.loadingTestCases$ = of(false);
          this.toaster.pop({
            type: "error",
            text: MessageEnum.TEST_CASE_DELETION_FAILURE,
          });
        },
      });
  }

  onAddMissingTestCases() {
    this.openTestCaseModal("add");
  }

  onEditTestCase(row: any) {
    this.openTestCaseModal("edit", row);
  }

  private openTestCaseModal(mode: "add" | "edit", row?: any) {
    combineLatest([
      this.store.select(selectConnections).pipe(take(1)),
      this.storyIds$.pipe(take(1)),
      this.store.select(selectAllTestCasesByVersionId).pipe(take(1)),
    ]).subscribe(([connections, storyIds, cases]) => {
      connections.LLM?.forEach((connection: any) => {
        if (connection.connectionName === this.suite?.llmName) {
          this.llmConnection = connection;
        }
      });

      const isBpdSource =
        (cases?.length ?? 0) > 0 &&
        (cases ?? []).every(
          (tc: any) => String(tc?.source ?? "").toUpperCase() === "BPD"
        );

      const isEdit = mode === "edit";
      const modalRef = this.modalService.open(AddMissingTestCaseModalComponent, {
        centered: true,
        size: "md",
        backdrop: "static",
      });
      modalRef.componentInstance.configs = {
        title: isEdit ? "Edit Test Case" : "Add Missing Test Cases",
        saveBtnText: isEdit ? "Update" : "Add",
        submittingText: isEdit ? "Updating..." : "Adding...",
        data: {
          storyIds,
          disableUserStoryFields: isBpdSource,
          initialValues: isEdit ? this.rowToFormValues(row) : null,
        },
        onSubmit: (formData: any) => {
          const data = isBpdSource ? { ...formData, source: "BPD" } : formData;
          return isEdit
            ? this.updateTestCase(data, modalRef)
            : this.addTestCase(data, modalRef);
        },
      };
      modalRef.result.then(
        () => {},
        () => {
          if (modalRef.componentInstance.isSubmitting) {
            modalRef.componentInstance.isSubmitting = false;
          }
        }
      );
    });
  }

  private rowToFormValues(row: any) {
    return {
      _id: row?._id,
      storyId: row?.userStoryId ?? "",
      userStory: row?.storydesc ?? row?.storysummary ?? "",
      testCaseId: row?.testCaseId ?? "",
      testCaseDescription: row?.testCase ?? "",
      testCaseType: row?.testCaseType ?? row?.testType ?? "Smoke",
      subType: row?.subType ?? "happy",
      prerequisites: row?.prerequisites ?? "",
      testSteps: row?.testScenario ?? row?.steps ?? "",
      sanitizedTestSteps: row?.sanitizedTestSteps ?? "",
      expectedResult: row?.expectedResult ?? "",
    };
  }

  private buildTestCasePayload(value: any) {
    const storyId = String(value?.storyId ?? "").trim() || "BPD";
    const userStory = String(value?.userStory ?? "").trim() || "BPD";
    const payload: any = {
      _id: value?._id,
      testCaseId: value?.testCaseId,
      testCase: value?.testCaseDescription,
      testScenario: value?.testSteps,
      expectedResult: value?.expectedResult,
      prerequisites: value?.prerequisites,
      steps: value?.testSteps,
      sanitizedTestSteps: value?.sanitizedTestSteps ?? "",
      key: storyId,
      storydesc: userStory,
      storysummary: userStory,
      testCaseType: value?.testCaseType || "Smoke",
      subType: value?.subType || "happy",
    };
    if (value?.source) {
      payload.source = value.source;
    }
    return payload;
  }

  addTestCase(value: any, modalRef?: NgbModalRef) {
    const versionId = this.suite?.id;
    if (!versionId) {
      if (modalRef) {
        modalRef.componentInstance.isSubmitting = false;
      }
      return;
    }

    const payload = {
      versionName: this.suite?.testSuiteName,
      selectedVersionId: versionId,
      llmConnectionId: this.llmConnection?.id,
      isNewTestSuite: false,
      testCases: [this.buildTestCasePayload(value)],
    };

    this.loadingTestCases$ = of(true);
    this.restService.addMissingTestCase(this.projectId, payload).subscribe({
      next: () => {
        this.toaster.pop({ type: "success", text: MessageEnum.TEST_CASE_CREATION_SUCCESS });
        this.store.dispatch(
          TestCaseGeneratorActions.loadTestCaseDetailsByVersionId({
            projectId: this.projectId,
            versionId: versionId,
          })
        );

        this.loadingTestCases$ = of(false);
        modalRef?.close();
      },
      error: () => {
        this.loadingTestCases$ = of(false);
        if (modalRef) {
          modalRef.componentInstance.isSubmitting = false;
        }
        this.toaster.pop({ type: "error", text: MessageEnum.TEST_CASE_CREATION_FAILURE });
      },
    });
  }

  updateTestCase(value: any, modalRef?: NgbModalRef) {
    const versionId = this.suite?.id;
    if (!versionId) {
      if (modalRef) {
        modalRef.componentInstance.isSubmitting = false;
      }
      return;
    }

    this.loadingTestCases$ = of(true);
    this.restService.editTestCase(this.projectId, versionId, [this.buildTestCasePayload(value)]).subscribe({
      next: (response: any) => {
        if (response?.status === 201) {
          this.toaster.pop({ type: "success", text: MessageEnum.TEST_CASE_UPDATED_SUCCESS });
          this.store.dispatch(
            TestCaseGeneratorActions.loadTestCaseDetailsByVersionId({
              projectId: this.projectId,
              versionId: versionId,
            })
          );
          modalRef?.close();
        } else if (modalRef) {
          modalRef.componentInstance.isSubmitting = false;
        }
        this.loadingTestCases$ = of(false);
      },
      error: () => {
        this.loadingTestCases$ = of(false);
        if (modalRef) {
          modalRef.componentInstance.isSubmitting = false;
        }
        this.toaster.pop({ type: "error", text: MessageEnum.TEST_CASE_UPDATED_FAILURE });
      },
    });
  }

  onAccordionAction(event: { type: string; data?: any; index?: number }): void {
    switch (event?.type) {
      case "edit":
        this.onEditTestCase(event.data);
        break;
      case "filter":
        this.onFilter();
        break;
      case "download":
        this.onDownload(event);
        break;
      case "delete":
        this.onDelete(event);
        break;
      case "add_missing":
        this.onAddMissingTestCases();
        break;
      case "import_test_cases":
        this.importTestCases.emit({
          suiteId: this.suite?.id ?? '',
          suiteName: this.suite?.testSuiteName ?? '',
          llmConnectionId: this.llmConnection?.id ?? this.llmConnection ?? null,
        });
        break;
      case "add_to_suite":
        this.onAddToSuite(event.data ?? []);
        break;
      case "feedback_submit":
        this.addFeedback(event);
        break;
      case "open-traceability":
        this.openTraceability(event.data);
        break;
      case "open-user-story":
        this.openUserStory(event.data);
        break;
    }
  }

  openUserStory(data:any){
    const modalRef = this.modalService.open(TcgStoryDetailModalComponent, {
      centered: true,
      windowClass: "user-story-modal",
      size: "xl",
      backdrop: 'static',
    });
    modalRef.componentInstance.data = data;
    modalRef.componentInstance.contentList = userstoryDetails;
    modalRef.componentInstance.title = "User Story Details";
  }

  private openTraceability(data: { traceability?: string; key?: string }): void {
    if (!data?.traceability || !data?.key) {
      return;
    }

    const modalRef = this.modalService.open(TcgTraceabilityModalComponent, {
      centered: true,
      windowClass: "user-story-modal",
      size: "m",
      backdrop: "static",
    });
    modalRef.componentInstance.data = data;
    modalRef.componentInstance.title = "Traceability";
  }

  addFeedback(event: any): void {
    if (this.isUpdatingFeedback) {
      return;
    }

    const versionId = this.suite?.id;
    const feedback = (event?.feedback ?? '').toString().trim();
    const testcaseId = event?.data?.testCaseId ?? event?.data?.id ?? event?.data?._id;

    if (!versionId) {
      this.toaster.pop({ type: 'error', text: 'Missing test case version.' });
      return;
    }
    if (!feedback || !testcaseId) {
      this.toaster.pop({ type: 'error', text: 'Please enter feedback before updating.' });
      return;
    }

    const payload = [{
      projectId: this.projectId,
      testcaseId,
      userInput: feedback,
      versionId,
    }];

    this.isUpdatingFeedback = true;
    this.restService.updateTestCase(payload).pipe(
      switchMap(() =>
        this.restService.getTestCaseByVersionId(this.projectId, versionId)
      ),
      take(1)
    ).subscribe({
      next: (res: any) => {
        const refreshed = (res?.data?.testCases ?? []).map((tc: any, index: number) => ({
          ...tc,
          // Keep BPD / accordion field aliases consistent with initial load
          key: tc.key ?? tc.userStoryId ?? tc.storyId ?? '',
          userStoryId: tc.userStoryId ?? tc.key ?? tc.storyId ?? '',
          testType: tc.testType ?? tc.testCaseType ?? tc.type ?? '',
          subType: tc.subType ?? '',
          priority: tc.priority ?? '',
          feedback: '',
          _id:
            tc?._id ??
            `${tc?.key || tc?.userStoryId || 'story'}__${tc?.testCaseId || 'tc'}__${index}`,
        }));

        this.store.dispatch(
          TestCaseGeneratorActions.loadTestCaseDetailsByVersionIdSuccess({
            testCases: refreshed,
          })
        );
        this.isUpdatingFeedback = false;
        this.toaster.pop({ type: 'success', text: MessageEnum.FEEDBACK_SUBMIT_SUCCESS });
      },
      error: (err: any) => {
        this.isUpdatingFeedback = false;
        console.error('[TCG] Update from feedback failed:', err);
        this.toaster.pop({
          type: 'error',
          text: err?.error?.message || MessageEnum.FEEDBACK_SUBMIT_FAILURE,
        });
      },
    });
  }

  onAddToSuite(selectedRows: any[]): void {
    if (!selectedRows.length) {
      this.toaster.pop({ type: 'error', text: MessageEnum.DATA_SELECTION_NONE });
      return;
    }

    // In the import flow, delegate to the parent which opens the dedicated modal.
    if (this.isImportFlow) {
      this.importAddToSuite.emit(selectedRows);
      return;
    }

    const versionId = this.suite?.id;
    if (!versionId) {
      this.toaster.pop({ type: 'error', text: 'Missing test case version. Please regenerate test cases.' });
      return;
    }

    // Same source as generated-test-cases addtestSuite
    this.commonService.llmConnection$.pipe(take(1)).subscribe((connection) => {
      this.llmConnection = connection;
    });

    const modalRef = this.modalService.open(AddTestSuiteComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'add-item-modal',
    });

    modalRef.componentInstance.data = {
      fields: [
        {
          fieldGroupClassName: 'row',
          fieldGroup: [
            {
              className: 'col-md-12',
              key: 'testSuiteName',
              type: 'input',
              templateOptions: { label: 'Test Suite Name', required: true },
              validation: { messages: { required: () => 'This field is required.' } },
            },
          ],
        },
      ],
    };

    modalRef.result.then((result: any) => {
      if (!result?.testSuite) {
        return;
      }

      this.testCaseVersions$.pipe(take(1)).subscribe((versionConnections) => {
        const versionExists = versionConnections.some(
          (version) => version.label === result?.testSuite
        );

        if (versionExists) {
          this.toaster.pop({ type: 'error', text: MessageEnum.TEST_SUITE_NAME_DUPLICATE });
          modalRef.dismiss();
          this.onAddToSuite(selectedRows);
          return;
        }

        this.store.select(selectConnections).pipe(take(1)).subscribe((connections) => {
          // Prefer commonService value (same as generated-test-cases); fall back to resolved LLM id
          let llmConnectionId = this.llmConnection;
          if (!llmConnectionId) {
            llmConnectionId = this.resolveLlmConnectionId(connections);
            if (llmConnectionId) {
              this.commonService.setLLMConnection(llmConnectionId);
              this.llmConnection = llmConnectionId;
            }
          }

          // Match generated-test-cases formattedData (line ~420)
          const formattedData = {
            _id: versionId,
            versionName: result?.testSuite || '',
            selectedVersionId: null,
            isNewTestSuite: !!result?.testSuite,
            llmConnectionId: this.llmConnection,
            testCases: selectedRows.map((row: any) => this.toPersistTestCase(row)),
          };

          this.restService.persistTestCase(this.projectId, formattedData).subscribe({
            next: (response: any) => {
              if (response?.status === 201) {
                this.toaster.pop({ type: 'success', text: MessageEnum.TEST_SUITE_CREATION_SUCCESS });
                this.commonService.notifyVersionUpdate();
                this.back.emit();
              }
            },
            error: (error: any) => {
              console.error('Persist API Error:', error);
              this.toaster.pop({ type: 'error', text: MessageEnum.TEST_SUITE_CREATION_FAILURE });
            },
          });
        });
      });
    }).catch(() => { /* dismissed */ });
  }

  /**
   * Build persist payload from a selected accordion row.
   * Restores API testCase/testScenario when BPD display mapping swapped them,
   * and keeps the rest of the generated test-case fields.
   */
  private toPersistTestCase(row: any): any {
    const {
      feedback,
      userStoryId,
      testType,
      ...rest
    } = row ?? {};

    return {
      ...rest,
      key: rest.key ?? userStoryId ?? row?.key ?? row?.userStoryId,
      testCase: rest.testCase ?? rest.testCase,
      expectedResult: rest.expectedResult ?? rest.expectedResult,
      testScenario: rest.testScenario ?? rest.testScenario,
      acceptancecriteria: rest.acceptancecriteria ?? rest.acceptanceCriteria,
    };
  }

  /** Prefer suite/API value, then shared service, then first project LLM connection. */
  private resolveLlmConnectionId(connections: any): string | null {
    const fromSuite = this.suite?.llmConnectionId;
    if (fromSuite) {
      return fromSuite;
    }

    const fromService = this.commonService.getLLMConnection();
    if (typeof fromService === 'string' && fromService.trim()) {
      return fromService;
    }
    if (fromService?.id) {
      return fromService.id;
    }

    if (this.llmConnection?.id) {
      return this.llmConnection.id;
    }
    if (typeof this.llmConnection === 'string' && this.llmConnection.trim()) {
      return this.llmConnection;
    }

    const llmList = connections?.LLM ?? [];
    if (this.suite?.llmName) {
      const matched = llmList.find(
        (c: any) => c.connectionName === this.suite?.llmName
      );
      if (matched?.id) {
        return matched.id;
      }
    }

    return llmList[0]?.id ?? llmList[0]?._id ?? null;
  }
}
